import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useAbsenceExport } from '@/hooks/absence/useAbsenceExport';
import { formatDateToYYYYMMDD } from '@/utils';
import { absenceService } from '@/api/domains/absence/absence.service';
import {
  getAbsenceTypeOptions,
  getAbsenceStatusOptions,
} from '@/api/domains/absence/absence.types';
import type {
  AbsenceItem,
  AbsenceTotals,
  AbsenceType,
  AbsenceStatus,
} from '@/api/domains/absence/absence.types';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import colors from '@/assets/styles';


export interface AbsenceListFilters {
  from?: string;
  to?: string;
  tipoAusentismo: AbsenceType;
  estado: AbsenceStatus;
}

export const useAbsenceList = () => {
  const { t: translate } = useTranslation('absence');
  const { showApiError, openSidePanel } = useUI();

  const defaultFrom = useMemo(() => {
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - 1);
    return formatDateToYYYYMMDD(fromDate);
  }, []);

  const defaultTo = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  // Domain-specific totals that live alongside the generic pagination state.
  const [totals, setTotals] = useState<AbsenceTotals>({
    licencias: 0,
    vacaciones: 0,
    permisosEspeciales: 0,
    permisosAdministrativos: 0,
    permisosFraccion: 0,
    faltas: 0,
  });

  const emptyTotals: AbsenceTotals = useMemo(
    () => ({
      licencias: 0,
      vacaciones: 0,
      permisosEspeciales: 0,
      permisosAdministrativos: 0,
      permisosFraccion: 0,
      faltas: 0,
    }),
    [],
  );

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AbsenceListFilters>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AbsenceItem> | null> => {
      const fromDateString = params.filters.from ?? defaultFrom;
      const toDateString = params.filters.to ?? defaultTo;

      const response = await absenceService.list({
        startDate: fromDateString,
        endDate: toDateString,
        absenceType: params.filters.tipoAusentismo,
        status: params.filters.estado,
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText ?? null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('errors.loadFailed'));
        setTotals(emptyTotals);
        return null;
      }

      setTotals(response.data.totals);
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [defaultFrom, defaultTo, showApiError, translate, emptyTotals],
  );

  const {
    rows,
    total: totalRecords,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
  } = usePaginatedList<AbsenceItem, AbsenceListFilters>({
    fetchFn,
    initialFilters: {
      from: formatDateToYYYYMMDD(new Date(new Date().setMonth(new Date().getMonth() - 1))),
      to: formatDateToYYYYMMDD(new Date()),
      tipoAusentismo: 'todos' as AbsenceType,
      estado: 'todos' as AbsenceStatus,
    },
    initialSort: { key: 'createdAt', direction: 'DESC' },
    orderByMap: {
      tipo: 'type',
      descripcion: 'type',
      fechaDesde: 'startDate',
      fechaHasta: 'endDate',
      estado: 'status',
      fechaCreacion: 'createdAt',
      creadoPor: 'createdBy',
    },
  });

  // Export hook
  const { exportAbsences, isExporting } = useAbsenceExport({
    startDate: filters.from ?? defaultFrom,
    endDate: filters.to ?? defaultTo,
    absenceType: filters.tipoAusentismo,
    status: filters.estado,
  });

  const handleFilterChange = useCallback(
    (key: string, value: string | string[]) => {
      setFilters((prev) => ({ ...prev, [key]: value as string }));
    },
    [setFilters],
  );

  // Filter options for dropdowns (from API layer)
  const typeOptions = useMemo(() => getAbsenceTypeOptions(translate), [translate]);
  const statusOptions = useMemo(() => getAbsenceStatusOptions(translate), [translate]);

  const filterDefinitions = useMemo(
    () => [
      {
        key: 'tipoAusentismo',
        label: translate('filters.type'),
        options: typeOptions,
        defaultValue: 'todos',
      },
      {
        key: 'estado',
        label: translate('filters.status'),
        options: statusOptions,
        defaultValue: 'todos',
      },
    ],
    [translate, typeOptions, statusOptions],
  );

  // Table columns
  const columns: TableColumn<AbsenceItem>[] = useMemo(
    () => [
      {
        key: 'tipo',
        header: translate('table.columns.tipo'),
        sortable: true,
      },
      {
        key: 'fechaCreacion',
        header: translate('table.columns.fechaCreacion'),
        sortable: true,
      },
      {
        key: 'fechaDesde',
        header: translate('table.columns.fechaDesde'),
        sortable: true,
      },
      {
        key: 'fechaHasta',
        header: translate('table.columns.fechaHasta'),
        sortable: true,
      },
      {
        key: 'estado',
        header: translate('table.columns.estado'),
        sortable: true,
      },
      {
        key: 'descripcion',
        header: translate('table.columns.descripcion'),
        sortable: false,
      },
      {
        key: 'creadoPor',
        header: translate('table.columns.creadoPor'),
        sortable: false,
      },
    ],
    [translate],
  );

  // Row actions
  const actions: TableAction<AbsenceItem>[] = useMemo(
    () => [
      {
        id: 'view-detail',
        label: translate('actions.viewDetail'),
        icon: 'eye' as const,
        color: colors.important.main,
        onClick: (row: AbsenceItem) => {
          openSidePanel({
            title: translate('detail.title'),
            component: 'AbsenceDetailForm',
            props: { data: row },
          });
        },
      },
    ],
    [translate, openSidePanel],
  );

  return {
    // Filters
    filters,
    setFilters,
    defaultFrom,
    defaultTo,
    filterDefinitions,
    handleFilterChange,
    // Data (property names preserved for backwards compatibility)
    data: rows,
    totals,
    totalRecords,
    isLoading,
    // Pagination
    page,
    pageSize,
    setPage,
    setPageSize,
    // Search & Sort
    onSearch,
    onSortChange,
    // Table config
    columns,
    actions,
    // Export
    exportAbsences,
    isExporting,
    // i18n
    translate,
  };
};
